'use client';

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/core/presentation/components/ui/Button';
import { Input } from '@/core/presentation/components/ui/Input';
import AppIcon from '@/core/presentation/components/ui/AppIcon';
import { Spinner } from '@/core/presentation/components/ui/Spinner';
import { formatPhoneWithCountryCode, normalizePhoneToNationalFormatted } from '@/core/utils/formatPhone';
import { SectionScreenLayout } from './SectionScreenLayout';
import { useAuth } from '@/features/authentication/presentation/context/AuthContext';
import { useUser } from '@/features/app/user/presentation/context/UserContext';
import { useReminderSettings } from '@/features/app/reminder-settings/presentation/context/ReminderSettingsContext';
import { useToast } from '@/core/presentation/components/Toast';
import {
  REMINDER_WHATSAPP_TEST_INTEGRATION_NOTICE,
  REMINDER_WHATSAPP_TEST_INTEGRATION_VERIFIED,
  REMINDER_WHATSAPP_TEST_AWAITING_CONFIRMATION,
  REMINDER_WHATSAPP_TEST_RESEND_COUNTDOWN,
  REMINDER_WHATSAPP_TEST_SECTION_INTRO,
} from '@/features/app/presentation/constants/reminder-settings-whatsapp-test-copy';
import { useAwaitInitialFetch } from '@/features/app/presentation/hooks/use-await-initial-fetch';
import { APP_SECTION_LOADING_MESSAGE } from '@/features/app/presentation/constants/app-shell-copy';
import {
  REMINDER_SETTINGS_FOOTER_DOUBTS_INTRO,
  REMINDER_SETTINGS_FOOTER_FAQ_SUPPORT_LINK,
  REMINDER_WHATSAPP_TEST_PHONE_CHANGE_LINK,
  REMINDER_WHATSAPP_TEST_PHONE_CHANGE_PREFIX,
  REMINDER_WHATSAPP_TEST_PHONE_CHANGE_SUFFIX,
  REMINDER_WHATSAPP_TEST_PHONE_LABEL,
  REMINDER_WHATSAPP_TEST_PHONE_UNDEFINED,
} from '@/features/app/presentation/constants/reminder-settings-whatsapp-test-copy';
import { appSectionPath } from '@/app/routes';
import { APP_SECTION } from '@/features/app/constants/sections';
import {
  parsePreferredNotificationTime,
} from '@/core/domain';
import { cn } from '@/lib/utils';
import { useReminderSettingsProductDemo } from '@/features/app/product-demo/presentation/composables/useReminderSettingsProductDemo';
import { ReminderSettingsProductDemoLayer } from '@/features/app/product-demo/presentation/components/ReminderSettingsProductDemoLayer';
import {
  PRODUCT_DEMO_REMINDER_SETTINGS_SPOTLIGHT_PLACEMENT,
  PRODUCT_DEMO_REMINDER_WHATSAPP_SPOTLIGHT_PLACEMENT,
} from '@/features/app/product-demo/presentation/constants/product-demo-reminder-settings.constants';
import {
  REMINDER_SCHEDULE_ERR_OFFSET_EQUAL,
  REMINDER_SCHEDULE_ERR_OFFSET_ORDER,
  REMINDER_SCHEDULE_ERR_OFFSET_RANGE,
  REMINDER_SCHEDULE_ERR_TIME,
  REMINDER_SCHEDULE_FIRST_OFFSET_LABEL,
  REMINDER_SCHEDULE_OFFSETS_HINT,
  REMINDER_SCHEDULE_SAVE,
  REMINDER_SCHEDULE_SAVING,
  REMINDER_SCHEDULE_SECOND_OFFSET_LABEL,
  REMINDER_SCHEDULE_SECTION_INTRO,
  REMINDER_SCHEDULE_SECTION_TITLE,
  REMINDER_SETTINGS_SECTION_SUBTITLE,
  REMINDER_SCHEDULE_TIME_HINT,
  REMINDER_SCHEDULE_TIME_LABEL,
  REMINDER_SCHEDULE_TOAST_OK,
} from '@/features/app/presentation/constants/reminder-settings-schedule-copy';

function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

export const ReminderSettingsSection: React.FC = () => {
  const { state: authState } = useAuth();
  const { state: userState, events: userEvents } = useUser();
  const { state: reminderState, events: reminderEvents } = useReminderSettings();
  const toast = useToast();

  const [sendTestError, setSendTestError] = useState(false);
  const [sendTestLoading, setSendTestLoading] = useState(false);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [preferredTime, setPreferredTime] = useState('09:00');
  const [offsetEarly, setOffsetEarly] = useState('7');
  const [offsetLate, setOffsetLate] = useState('1');
  const [awaitingWebhookConfirm, setAwaitingWebhookConfirm] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState<number | null>(null);
  const testSendedUnsubRef = useRef<(() => void) | null>(null);

  const productDemo = useReminderSettingsProductDemo();

  const phoneNational = useMemo(
    () => normalizePhoneToNationalFormatted(userState.currentUser?.phone ?? '') ?? '',
    [userState.currentUser?.phone]
  );

  const phoneDisplay = useMemo(() => {
    if (!phoneNational) return REMINDER_WHATSAPP_TEST_PHONE_UNDEFINED;
    return formatPhoneWithCountryCode(phoneNational);
  }, [phoneNational]);

  const stopTestSendedListener = useCallback(() => {
    testSendedUnsubRef.current?.();
    testSendedUnsubRef.current = null;
  }, []);

  useEffect(() => {
    return () => {
      stopTestSendedListener();
    };
  }, [stopTestSendedListener]);

  useEffect(() => {
    if (!awaitingWebhookConfirm) return;
    let sec = 60;
    setCooldownSeconds(60);
    const id = window.setInterval(() => {
      sec -= 1;
      setCooldownSeconds(sec);
      if (sec <= 0) {
        window.clearInterval(id);
        stopTestSendedListener();
        setAwaitingWebhookConfirm(false);
        setCooldownSeconds(null);
      }
    }, 1000);
    return () => window.clearInterval(id);
  }, [awaitingWebhookConfirm, stopTestSendedListener]);

  const loadReminderSection = useCallback(async () => {
    if (!authState.authReady || !authState.user?.uid) return;
    const uid = authState.user.uid;
    await Promise.all([reminderEvents.getByUserId(), userEvents.getUserById(uid)]);
  }, [
    authState.authReady,
    authState.user?.uid,
    reminderEvents.getByUserId,
    userEvents.getUserById,
  ]);

  const sectionBootstrapReady = useAwaitInitialFetch(loadReminderSection, [loadReminderSection]);

  useEffect(() => {
    const s = reminderState.currentSettings;
    if (s) {
      setPreferredTime(s.preferred_notification_time);
      setOffsetEarly(String(s.reminder_offsets_days[0]));
      setOffsetLate(String(s.reminder_offsets_days[1]));
    } else if (!reminderState.loading && sectionBootstrapReady) {
      setPreferredTime('09:00');
      setOffsetEarly('7');
      setOffsetLate('1');
    }
  }, [reminderState.currentSettings, reminderState.loading, sectionBootstrapReady]);

  useEffect(() => {
    if (reminderState.currentSettings?.message_test_sended) {
      stopTestSendedListener();
      setAwaitingWebhookConfirm(false);
      setCooldownSeconds(null);
      setSendTestError(false);
    }
  }, [reminderState.currentSettings?.message_test_sended, stopTestSendedListener]);

  const handleSendTest = () => {
    if (reminderState.currentSettings?.message_test_sended) return;

    void (async () => {
      setSendTestError(false);
      setSendTestLoading(true);

      const nationalDigits = digitsOnly(phoneNational);
      if (nationalDigits.length < 10) {
        setSendTestLoading(false);
        setSendTestError(true);
        toast.error('Informe um número válido com DDD para testar o envio.');
        return;
      }

      const to = nationalDigits.startsWith('55') ? nationalDigits : `55${nationalDigits}`;

      const sendResult = await reminderEvents.sendWhatsAppTestMessage({ to });
      setSendTestLoading(false);
      if (!sendResult.success) {
        setSendTestError(true);
        toast.error(sendResult.error ?? 'Não foi possível enviar a mensagem de teste.');
        return;
      }

      const uid = authState.user?.uid;
      if (!uid) {
        setSendTestError(true);
        toast.error('Sessão inválida. Faça login novamente.');
        return;
      }

      stopTestSendedListener();
      const unsub = await reminderEvents.subscribeMessageTestSendedFlag((sended) => {
        if (!sended) return;
        stopTestSendedListener();
        setAwaitingWebhookConfirm(false);
        setCooldownSeconds(null);
        void reminderEvents.getByUserId();
        toast.success('Integração com o WhatsApp verificada.');
      });
      if (!unsub) {
        toast.error('Não foi possível iniciar a escuta da confirmação.');
        return;
      }
      testSendedUnsubRef.current = unsub;
      setAwaitingWebhookConfirm(true);
    })();
  };

  const isValidPhone = digitsOnly(phoneNational).length >= 10;
  const integrationVerified = reminderState.currentSettings?.message_test_sended === true;
  const resendBlocked =
    awaitingWebhookConfirm && cooldownSeconds !== null && cooldownSeconds > 0;
  const canSendTest =
    isValidPhone &&
    !userState.loading &&
    !reminderState.loading &&
    !integrationVerified &&
    !sendTestLoading &&
    !resendBlocked;

  const loadingSettings = !!authState.user && reminderState.loading && !reminderState.currentSettings;

  const handleSaveSchedule = useCallback(() => {
    const early = Number.parseInt(offsetEarly, 10);
    const late = Number.parseInt(offsetLate, 10);
    if (
      !Number.isInteger(early) ||
      !Number.isInteger(late) ||
      early < 0 ||
      early > 365 ||
      late < 0 ||
      late > 365
    ) {
      toast.error(REMINDER_SCHEDULE_ERR_OFFSET_RANGE);
      return;
    }
    if (early === late) {
      toast.error(REMINDER_SCHEDULE_ERR_OFFSET_EQUAL);
      return;
    }
    if (early <= late) {
      toast.error(REMINDER_SCHEDULE_ERR_OFFSET_ORDER);
      return;
    }
    let timeValue: string;
    try {
      timeValue = parsePreferredNotificationTime(preferredTime);
    } catch {
      toast.error(REMINDER_SCHEDULE_ERR_TIME);
      return;
    }
    const tuple: [number, number] = [early, late];

    void (async () => {
      setScheduleSaving(true);
      const payload = {
        reminder_offsets_days: tuple,
        preferred_notification_time: timeValue,
      };
      const existing = reminderState.currentSettings;
      const result =
        existing != null ?
          await reminderEvents.update(payload)
        : await reminderEvents.create({
            ...payload,
            message_test_sended: false,
            message_test_outbound_wamid: null,
          });
      setScheduleSaving(false);
      if (result.success) {
        toast.success(REMINDER_SCHEDULE_TOAST_OK);
      } else {
        toast.error(result.error ?? 'Não foi possível salvar.');
      }
    })();
  }, [
    offsetEarly,
    offsetLate,
    preferredTime,
    reminderEvents,
    reminderState.currentSettings,
    toast,
  ]);

  return (
    <SectionScreenLayout
      title="Configurações de lembretes"
      subtitle={REMINDER_SETTINGS_SECTION_SUBTITLE}
    >
      {!sectionBootstrapReady ? (
        <p className="text-sm text-text-muted flex items-center gap-2 py-8">
          <Spinner size="sm" className="text-cyan" aria-hidden />
          <span>{APP_SECTION_LOADING_MESSAGE}</span>
        </p>
      ) : (
      <div className="space-y-8">
        {loadingSettings && (
          <p className="text-sm text-text-muted flex items-center gap-2">
            <Spinner size="sm" className="text-cyan" aria-hidden />
            <span>Carregando configurações</span>
          </p>
        )}
        {reminderState.error && (
          <p className="text-sm text-red-600 flex items-center gap-2">
            <AppIcon name="ExclamationCircleIcon" size={18} className="shrink-0" />
            {reminderState.error}
          </p>
        )}
        <section
          data-product-demo-reminder-schedule
          className={cn(
            'space-y-4 transition-shadow duration-200',
            productDemo.demoUiActive && productDemo.phase === 'schedule_spotlight' &&
              'relative z-[101] rounded-xl ring-2 ring-cyan ring-offset-2 ring-offset-[#f0f4f8]'
          )}
        >
          <h2 className="flex items-center gap-2 text-sm font-semibold text-navy-dark uppercase tracking-wide">
            <AppIcon name="ClockIcon" size={20} className="text-cyan shrink-0" />
            {REMINDER_SCHEDULE_SECTION_TITLE}
          </h2>
          <p className="text-text-body text-sm">{REMINDER_SCHEDULE_SECTION_INTRO}</p>
          <div className="grid gap-4 sm:grid-cols-2 sm:items-start">
            <div className="sm:col-span-2">
              <Input
                variant="light"
                type="time"
                label={REMINDER_SCHEDULE_TIME_LABEL}
                value={preferredTime}
                onChange={(e) => setPreferredTime(e.target.value)}
                fullWidth
              />
              <p className="mt-1.5 text-xs text-text-muted">{REMINDER_SCHEDULE_TIME_HINT}</p>
            </div>
            <Input
              variant="light"
              type="number"
              inputMode="numeric"
              min={0}
              max={365}
              step={1}
              label={REMINDER_SCHEDULE_FIRST_OFFSET_LABEL}
              value={offsetEarly}
              onChange={(e) => setOffsetEarly(e.target.value)}
              fullWidth
            />
            <Input
              variant="light"
              type="number"
              inputMode="numeric"
              min={0}
              max={365}
              step={1}
              label={REMINDER_SCHEDULE_SECOND_OFFSET_LABEL}
              value={offsetLate}
              onChange={(e) => setOffsetLate(e.target.value)}
              fullWidth
            />
          </div>
          <p className="flex items-start gap-2 text-xs text-text-muted -mt-2">
            <AppIcon name="InformationCircleIcon" size={16} className="text-cyan/70 shrink-0 mt-0.5" />
            <span>{REMINDER_SCHEDULE_OFFSETS_HINT}</span>
          </p>
          <div className="flex flex-col items-end gap-2">
            <Button
              type="button"
              variant="primary"
              size="lg"
              className="gap-2"
              disabled={reminderState.loading || scheduleSaving}
              loading={scheduleSaving}
              loadingLabel={REMINDER_SCHEDULE_SAVING}
              onClick={handleSaveSchedule}
            >
              <AppIcon name="ClipboardDocumentCheckIcon" size={20} aria-hidden />
              {REMINDER_SCHEDULE_SAVE}
            </Button>
          </div>
        </section>

        <section
          data-product-demo-whatsapp-test
          className={cn(
            'space-y-3 pt-6 border-t border-border-light transition-shadow duration-200',
            productDemo.demoUiActive && productDemo.phase === 'whatsapp_spotlight' &&
              'relative z-[101] rounded-xl ring-2 ring-cyan ring-offset-2 ring-offset-[#f0f4f8]'
          )}
        >
          <h2 className="flex items-center gap-2 text-sm font-semibold text-navy-dark uppercase tracking-wide">
            <AppIcon name="PaperAirplaneIcon" size={20} className="text-cyan shrink-0" />
            Testar envio
          </h2>
          <p className="text-text-body text-sm">{REMINDER_WHATSAPP_TEST_SECTION_INTRO}</p>
          <div>
            <div
              className="flex items-center gap-2 text-sm font-medium text-navy-dark mb-1.5 min-w-0"
              id="reminder-settings-test-phone-label"
            >
              <AppIcon name="PhoneIcon" size={18} className="text-text-muted shrink-0" />
              <span className="truncate">{REMINDER_WHATSAPP_TEST_PHONE_LABEL}</span>
            </div>
            <div
              className="rounded-lg px-4 py-3 bg-[#f0f4f8] border border-border-light text-text-body text-sm"
              role="status"
              aria-labelledby="reminder-settings-test-phone-label"
            >
              {phoneDisplay}
            </div>
            <p className="mt-1.5 flex items-start gap-2 text-xs text-text-muted">
              <AppIcon name="InformationCircleIcon" size={16} className="text-cyan/70 shrink-0 mt-0.5" />
              <span>
                {REMINDER_WHATSAPP_TEST_PHONE_CHANGE_PREFIX}{' '}
                <Link
                  to={appSectionPath(APP_SECTION.MINHA_CONTA)}
                  className="text-cyan hover:text-cyan-dark underline focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan focus-visible:ring-offset-1 rounded"
                >
                  {REMINDER_WHATSAPP_TEST_PHONE_CHANGE_LINK}
                </Link>
                {REMINDER_WHATSAPP_TEST_PHONE_CHANGE_SUFFIX}
              </span>
            </p>
          </div>
          {!integrationVerified && (
            <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
              <AppIcon name="InformationCircleIcon" size={22} className="text-amber-700 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-900">{REMINDER_WHATSAPP_TEST_INTEGRATION_NOTICE}</p>
            </div>
          )}
          {integrationVerified && (
            <div className="flex items-start gap-3 p-3 rounded-lg bg-green-50 border border-green-200">
              <AppIcon name="CheckCircleIcon" size={22} className="text-green-700 shrink-0 mt-0.5" />
              <p className="text-sm text-green-900">{REMINDER_WHATSAPP_TEST_INTEGRATION_VERIFIED}</p>
            </div>
          )}
          {awaitingWebhookConfirm && !integrationVerified && (
            <div className="flex flex-col gap-2 p-3 rounded-lg bg-[#eef6ff] border border-cyan/30">
              <div className="flex items-start gap-3">
                <Spinner size="sm" className="text-cyan shrink-0 mt-1" aria-hidden />
                <p className="text-sm text-navy-dark leading-relaxed">{REMINDER_WHATSAPP_TEST_AWAITING_CONFIRMATION}</p>
              </div>
              {cooldownSeconds !== null && cooldownSeconds > 0 ? (
                <p className="text-xs text-text-muted pl-8">{REMINDER_WHATSAPP_TEST_RESEND_COUNTDOWN(cooldownSeconds)}</p>
              ) : null}
            </div>
          )}
          <div className="flex flex-col items-end gap-2">
            {!integrationVerified ? (
              <Button
                type="button"
                onClick={handleSendTest}
                size="lg"
                className="gap-2"
                disabled={!canSendTest || sendTestLoading}
                loading={sendTestLoading}
                loadingLabel="Enviando…"
              >
                <AppIcon name="ChatBubbleLeftRightIcon" size={20} aria-hidden />
                Enviar mensagem de teste
              </Button>
            ) : null}
          </div>
          {sendTestError && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-red-50 border border-red-200">
              <AppIcon name="ExclamationCircleIcon" size={24} className="text-red-600 shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-800">Não foi possível enviar</p>
                <p className="text-sm text-red-700">
                  Tente novamente em instantes. Verifique se o número está correto.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => setSendTestError(false)}
              >
                <AppIcon name="ArrowPathIcon" size={16} />
                Tentar novamente
              </Button>
            </div>
          )}
          <div className="space-y-1 pt-2 flex flex-col items-end text-right">
            <p className="text-text-muted text-xs">
              {REMINDER_SETTINGS_FOOTER_DOUBTS_INTRO}{' '}
              <Link
                to={appSectionPath(APP_SECTION.FAQ_SUPORTE)}
                className="text-cyan hover:text-cyan-dark underline focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan focus-visible:ring-offset-1 rounded"
              >
                {REMINDER_SETTINGS_FOOTER_FAQ_SUPPORT_LINK}
              </Link>
              .
            </p>
          </div>
        </section>

        
      </div>
      )}
      {productDemo.demoUiActive ?
        <ReminderSettingsProductDemoLayer
          demoUiActive={productDemo.demoUiActive}
          phase={productDemo.phase}
          completeWelcome={productDemo.completeWelcome}
          completeWhatsappSpotlight={productDemo.completeWhatsappSpotlight}
          completeSpotlight={productDemo.completeSpotlight}
          skipDemo={productDemo.skipDemo}
          whatsappContinueEnabled={integrationVerified || productDemo.previewMode}
          whatsappSpotlightPlacement={PRODUCT_DEMO_REMINDER_WHATSAPP_SPOTLIGHT_PLACEMENT}
          spotlightPlacement={PRODUCT_DEMO_REMINDER_SETTINGS_SPOTLIGHT_PLACEMENT}
        />
      : null}
    </SectionScreenLayout>
  );
};
